import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  usePendingMutation,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

export type IntercomPerson = {
  id: string;
  firstName: string;
  lastName: string;
  personType: string;
  phone?: string | null;
  unityUsername?: string | null;
};

export type IntercomProfile = {
  id: string;
  projectID: string;
  name: string;
  channelConfig: Record<string, { talk: boolean; listen: boolean }>;
};

export type IntercomAssignment = {
  id: string;
  projectID: string;
  personID?: string | null;
  profileID?: string | null;
  unityUsername?: string | null;
  displayName?: string | null;
  person?: IntercomPerson | null;
  profile?: IntercomProfile | null;
};

export type IntercomChannel = {
  id: string;
  projectID: string;
  name: string;
  description?: string | null;
  channelNumber?: number | null;
  sortOrder: number;
};

export function useProjectIntercomChannels(projectId: string | undefined) {
  const query = projectId
    ? appQueries.intercomChannels.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });

  const channels = (data ?? []) as IntercomChannel[];

  return {
    data: channels,
    isLoading:
      !!projectId && details.type !== "complete" && channels.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useProjectIntercomAssignments(projectId: string | undefined) {
  const query = projectId
    ? appQueries.intercomAssignments.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });

  const assignments = (data ?? []) as IntercomAssignment[];

  return {
    data: assignments,
    isLoading:
      !!projectId && details.type !== "complete" && assignments.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useProjectIntercomProfiles(projectId: string | undefined) {
  const query = projectId
    ? appQueries.intercomProfiles.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });

  const profiles = (data ?? []) as IntercomProfile[];

  return {
    data: profiles,
    isLoading:
      !!projectId && details.type !== "complete" && profiles.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateIntercomChannel() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (input: {
    projectID: string;
    name: string;
    description?: string | null;
    channelNumber?: number | null;
    sortOrder?: number;
  }) =>
    runPending(async () => {
      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.intercomChannels.create({
          id,
          projectID: input.projectID,
          name: input.name,
          description: input.description ?? null,
          channelNumber: input.channelNumber ?? null,
          sortOrder: input.sortOrder ?? 0,
        }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Channel create was rejected",
      );
      return id;
    });

  return {
    mutate: (
      input: {
        projectID: string;
        name: string;
        description?: string | null;
        channelNumber?: number | null;
        sortOrder?: number;
      },
      options?: { onSuccess?: (id: string) => void },
    ) => {
      void run(input)
        .then((id) => {
          toast.success("Channel created");
          options?.onSuccess?.(id);
        })
        .catch((error) =>
          toast.error("Failed to create channel: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateIntercomChannel() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (input: {
    id: string;
    name?: string;
    description?: string | null;
    channelNumber?: number | null;
    sortOrder?: number;
  }) =>
    runPending(async () => {
      const mutation = zero.mutate(appMutators.intercomChannels.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Channel update was rejected",
      );
    });

  return {
    mutate: (input: {
      id: string;
      name?: string;
      description?: string | null;
      channelNumber?: number | null;
      sortOrder?: number;
    }) => {
      void run(input).catch((error) =>
        toast.error("Failed to update channel: " + error.message),
      );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteIntercomChannel() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (id: string) =>
    runPending(async () => {
      const mutation = zero.mutate(appMutators.intercomChannels.delete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Channel delete was rejected",
      );
    });

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Channel deleted");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete channel: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useCreateIntercomAssignment() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (input: {
    projectID: string;
    personID?: string | null;
    profileID?: string | null;
    unityUsername?: string | null;
    displayName?: string | null;
  }) =>
    runPending(async () => {
      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.intercomAssignments.create({
          id,
          projectID: input.projectID,
          personID: input.personID ?? null,
          profileID: input.profileID ?? null,
          unityUsername: input.unityUsername ?? null,
          displayName: input.displayName ?? null,
        }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Assignment create was rejected",
      );
      return id;
    });

  return {
    mutate: (
      input: {
        projectID: string;
        personID?: string | null;
        profileID?: string | null;
        unityUsername?: string | null;
        displayName?: string | null;
      },
      options?: { onSuccess?: (id: string) => void },
    ) => {
      void run(input)
        .then((id) => {
          toast.success("User added");
          options?.onSuccess?.(id);
        })
        .catch((error) => toast.error("Failed to add user: " + error.message));
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateIntercomAssignment() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (input: {
    id: string;
    profileID?: string | null;
    unityUsername?: string;
    displayName?: string | null;
  }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.intercomAssignments.update(input),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Assignment update was rejected",
      );
    });

  return {
    mutate: (input: {
      id: string;
      profileID?: string | null;
      unityUsername?: string;
      displayName?: string | null;
    }) => {
      void run(input).catch((error) =>
        toast.error("Failed to update user: " + error.message),
      );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteIntercomAssignment() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (id: string) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.intercomAssignments.delete({ id }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Assignment delete was rejected",
      );
    });

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("User removed");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to remove user: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useBulkAssignIntercomProfile() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (input: { profileID: string | null; assignmentIDs: string[] }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.intercomAssignments.bulkAssignProfile(input),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Bulk assign was rejected",
      );
    });

  return {
    mutate: (
      input: { profileID: string | null; assignmentIDs: string[] },
      options?: { onSuccess?: () => void },
    ) => {
      void run(input)
        .then(() => {
          toast.success("Profile assigned");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to assign profile: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useCreateIntercomProfile() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (input: {
    projectID: string;
    name: string;
    channelConfig: Record<string, { talk: boolean; listen: boolean }>;
  }) =>
    runPending(async () => {
      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.intercomProfiles.create({
          id,
          projectID: input.projectID,
          name: input.name,
          channelConfig: input.channelConfig,
        }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Profile create was rejected",
      );
      return id;
    });

  return {
    mutate: (
      input: {
        projectID: string;
        name: string;
        channelConfig: Record<string, { talk: boolean; listen: boolean }>;
      },
      options?: { onSuccess?: (id: string) => void },
    ) => {
      void run(input)
        .then((id) => {
          toast.success("Profile created");
          options?.onSuccess?.(id);
        })
        .catch((error) =>
          toast.error("Failed to create profile: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateIntercomProfile() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (input: {
    id: string;
    name?: string;
    channelConfig?: Record<string, { talk: boolean; listen: boolean }>;
  }) =>
    runPending(async () => {
      const mutation = zero.mutate(appMutators.intercomProfiles.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Profile update was rejected",
      );
    });

  return {
    mutate: (input: {
      id: string;
      name?: string;
      channelConfig?: Record<string, { talk: boolean; listen: boolean }>;
    }) => {
      void run(input).catch((error) =>
        toast.error("Failed to update profile: " + error.message),
      );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteIntercomProfile() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();

  const run = (id: string) =>
    runPending(async () => {
      const mutation = zero.mutate(appMutators.intercomProfiles.delete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Profile delete was rejected",
      );
    });

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Profile deleted");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete profile: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
